import  { useState, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Stars } from '@react-three/drei';
import * as THREE from 'three';
import Sun from './components/Sun';
import OrbitPaths from './components/OrbitPaths';
import Planet from './components/Planet';
import AsteroidBelt from './components/AsteroidBelt';
import PlanetInfoPanel from './components/PlanetInfoPanel';
import TimeControls from './components/TimeControls';
import CameraController from './components/CameraController';
import planetData from './data/planetData';

// Main Solar System component
export default function SolarSystem() {
  const [selectedPlanet, setSelectedPlanet] = useState(null);
  const [planetInfoPanel, setPlanetInfoPanel] = useState(null);
  const [timeSpeed, setTimeSpeed] = useState(1);
  const [isPaused, setIsPaused] = useState(false);

  const handlePlanetClick = useCallback((planet, position) => {
    // console.log('handlePlanetClick called:', planet.name, position); // Debug log
    setSelectedPlanet(planet);
    setPlanetInfoPanel({ planet, position });
  }, []);

  const closePlanetInfo = useCallback(() => {
    // console.log('Closing planet info'); // Debug log
    setPlanetInfoPanel(null);
    setSelectedPlanet(null);
  }, []);

  // Handle outside clicks to close info panel - but don't interfere with planet clicks
  const handleCanvasClick = useCallback((event) => {
    // Only close if we didn't click on a planet
    if (planetInfoPanel && !event.target.closest('mesh')) {
      closePlanetInfo();
    }
  }, [planetInfoPanel, closePlanetInfo]);

  return (
    <div style={{ width: '100vw', height: '100vh', background: '#000011', position: 'relative' }}>
      <Canvas
        camera={{ position: [0, 10, 15], fov: 60 }}
        gl={{ antialias: true }}
        shadows
        onPointerMissed={handleCanvasClick}
      >
        {/* Enhanced lighting */}
        <ambientLight intensity={0.05} />
        
        {/* Background stars */}
        <Stars radius={300} depth={60} count={8000} factor={7} saturation={0} fade />
        
        {/* Solar system components */}
        <Sun />
        <OrbitPaths selectedPlanet={selectedPlanet} planetData={planetData}/>
        
        {/* Planets */}
        {planetData.map((planet, index) => (
          <Planet
            key={index}
            size={planet.size}
            distance={planet.distance}
            color={planet.color}
            rotationSpeed={planet.rotationSpeed}
            orbitSpeed={planet.orbitSpeed}
            name={planet.name}
            planetInfo={planet}
            onPlanetClick={handlePlanetClick}
            timeSpeed={timeSpeed}
            isPaused={isPaused}
            isSelected={selectedPlanet && selectedPlanet.name === planet.name}
          />
        ))}
        
        {/* Space elements */}
        <AsteroidBelt />
        
        {/* Camera controller */}
        <CameraController selectedPlanet={selectedPlanet} />
        
        {/* Enhanced camera controls with touch support */}
        <OrbitControls
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minDistance={3}
          maxDistance={50}
          autoRotate={false}
          enableDamping={true}
          dampingFactor={0.05}
          touches={{
            ONE: THREE.TOUCH.ROTATE,
            TWO: THREE.TOUCH.DOLLY_PAN
          }}
        />
      </Canvas>
      
      {/* Enhanced UI */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        color: 'white',
        fontFamily: 'Arial, sans-serif',
        background: 'rgba(0,0,0,0.8)',
        padding: '15px',
        borderRadius: '12px',
        backdropFilter: 'blur(15px)',
        border: '1px solid rgba(255,255,255,0.1)',
        maxWidth: '250px'
      }}>
        <h2 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#FFD700' }}>🌌 Solar System Explorer</h2>
        <p style={{ margin: '5px 0', fontSize: '12px', opacity: 0.9 }}>🖱️ Click planets for detailed info</p>
        <p style={{ margin: '5px 0', fontSize: '12px', opacity: 0.9 }}>🔍 Scroll to zoom, drag to rotate</p>
        <p style={{ margin: '5px 0', fontSize: '12px', opacity: 0.9 }}>📱 Touch-friendly on mobile</p>
        <p style={{ margin: '5px 0', fontSize: '12px', opacity: 0.9 }}>⏯️ Use time controls below</p>
      </div>

      {/* Planet selection indicator */}
      {selectedPlanet && (
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          color: 'white',
          fontFamily: 'Arial, sans-serif',
          background: 'rgba(0,0,0,0.8)',
          padding: '10px 15px',
          borderRadius: '8px',
          backdropFilter: 'blur(10px)',
          border: `2px solid ${selectedPlanet.color}`,
          fontSize: '14px'
        }}>
          Selected: <span style={{ color: selectedPlanet.color, fontWeight: 'bold' }}>{selectedPlanet.name}</span>
        </div>
      )}
      
      {/* Time controls */}
      <TimeControls
        timeSpeed={timeSpeed}
        setTimeSpeed={setTimeSpeed}
        isPaused={isPaused}
        setIsPaused={setIsPaused}
      />
      
      {/* Planet info panel */}
      {planetInfoPanel && (
        <PlanetInfoPanel
          planet={planetInfoPanel.planet}
          position={planetInfoPanel.position}
          onClose={closePlanetInfo}
        />
      )}
    </div>
  );
}